import { useMutation, useQuery } from "@apollo/client";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  type ChipProps,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  SalesforceSyncPhase,
  SalesforceSyncRunsDocument,
  type SalesforceSyncRunsQuery,
  SalesforceSyncStatus,
  StartFullSalesforceSyncDocument,
  type StartFullSalesforceSyncMutation,
  StartSalesforceSyncDocument,
  type StartSalesforceSyncMutation,
} from "../generated/graphql";
import { formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { captureException } from "../lib/errorHandling";
import { salesforceIntegrationErrorKey } from "../lib/salesforceIntegration";
import { color, shadows, space } from "../theme/designTokens";
import { CustomConfirm } from "./CustomConfirm";

// Runs start in the worker; polling shows a queued run once it starts.
export const SALESFORCE_SYNC_RUNS_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;

// GraphQL codegen sorts enum members alphabetically; execution order is explicit.
const PHASES = [
  SalesforceSyncPhase.Reading,
  SalesforceSyncPhase.Accounts,
  SalesforceSyncPhase.Inventory,
  SalesforceSyncPhase.Communication,
  SalesforceSyncPhase.EmailBodies,
  SalesforceSyncPhase.Finalizing,
];

const EMPTY_CELL = "–";

type Run = SalesforceSyncRunsQuery["salesforceSyncRuns"][number];
type Counts = NonNullable<Run["counts"]>;

const COUNT_KEYS: readonly Exclude<keyof Counts, "__typename">[] = [
  "products",
  "accounts",
  "contracts",
  "items",
  "users",
  "contacts",
  "leads",
  "orders",
  "cases",
  "emails",
  "caseComments",
  "caseHistories",
  "calls",
];

const STATUS_COLOR: Record<SalesforceSyncStatus, ChipProps["color"]> = {
  [SalesforceSyncStatus.Running]: "info",
  [SalesforceSyncStatus.Succeeded]: "success",
  [SalesforceSyncStatus.Failed]: "error",
  [SalesforceSyncStatus.Skipped]: "warning",
};

const HEADER_KEYS = [
  "startedAt",
  "finishedAt",
  "trigger",
  "mode",
  "status",
  "anonymized",
  "records",
  "apiCalls",
  "errorCode",
] as const;

type SalesforceSyncRunsProps = {
  // The connection is saved, so a run can start.
  canStart: boolean;
};

export const SalesforceSyncRuns = ({ canStart }: SalesforceSyncRunsProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [startSync, { loading: starting }] = useMutation<StartSalesforceSyncMutation>(StartSalesforceSyncDocument);
  const [startFullSync, { loading: startingFull }] = useMutation<StartFullSalesforceSyncMutation>(
    StartFullSalesforceSyncDocument
  );
  const [confirmingFull, setConfirmingFull] = useState(false);
  const { data, loading, error, startPolling } = useQuery<SalesforceSyncRunsQuery>(SalesforceSyncRunsDocument, {
    fetchPolicy: "cache-and-network",
    pollInterval: IDLE_POLL_MS,
  });
  const runs = data?.salesforceSyncRuns;
  const isRunning = runs?.some(run => run.status === SalesforceSyncStatus.Running) ?? false;
  useEffect(() => {
    startPolling(isRunning ? SALESFORCE_SYNC_RUNS_POLL_MS : IDLE_POLL_MS);
  }, [isRunning, startPolling]);
  const title = translate("salesforceIntegration.runs.title");

  const start = useCallback(
    async (run: typeof startSync | typeof startFullSync, startedKey: string) => {
      try {
        await run({ refetchQueries: [{ query: SalesforceSyncRunsDocument }] });
        notify(translate(startedKey), { type: "success" });
      } catch (startError) {
        const key = salesforceIntegrationErrorKey(startError);
        if (key === null) captureException(startError, "salesforce/integration: start sync");
        notify(translate(key ?? "salesforceIntegration.runs.startError"), { type: "error" });
      }
    },
    [notify, translate]
  );

  const handleStart = useCallback(() => start(startSync, "salesforceIntegration.runs.started"), [start, startSync]);

  const handleStartFull = useCallback(async () => {
    setConfirmingFull(false);
    await start(startFullSync, "salesforceIntegration.runs.fullStarted");
  }, [start, startFullSync]);

  const startButtons = (
    <Stack direction="row" gap={space.xs}>
      <Button
        variant="outlined"
        onClick={() => setConfirmingFull(true)}
        disabled={!canStart || starting || startingFull}
      >
        {translate("salesforceIntegration.runs.startFull")}
      </Button>
      <Button
        variant="outlined"
        startIcon={<SyncOutlinedIcon />}
        onClick={handleStart}
        disabled={!canStart || starting || startingFull}
      >
        {translate("salesforceIntegration.runs.start")}
      </Button>
    </Stack>
  );

  const recordsCell = (run: Run) => {
    const { counts } = run;
    if (!counts) return EMPTY_CELL;
    const total = COUNT_KEYS.reduce((sum, key) => sum + counts[key], 0);
    // Zero counts are left out; an incremental run usually touches few objects.
    return (
      <>
        {formatCrmCount(total)}
        {COUNT_KEYS.filter(key => counts[key] > 0).map(key => (
          <Typography key={key} variant="caption" display="block" color="text.secondary" noWrap>
            {`${translate(`salesforceIntegration.runs.counts.${key}`)}: ${formatCrmCount(counts[key])}`}
          </Typography>
        ))}
      </>
    );
  };

  const apiCallsCell = (run: Run) => {
    const { apiCalls, apiRequestsRemaining, apiRequestsMax } = run;
    return (
      <>
        {apiCalls === null || apiCalls === undefined ? EMPTY_CELL : formatCrmCount(apiCalls)}
        {apiRequestsRemaining !== null &&
          apiRequestsRemaining !== undefined &&
          apiRequestsMax !== null &&
          apiRequestsMax !== undefined && (
            <Typography variant="caption" display="block" color="text.secondary">
              {translate("salesforceIntegration.runs.allowance", {
                remaining: formatCrmCount(apiRequestsRemaining),
                max: formatCrmCount(apiRequestsMax),
              })}
            </Typography>
          )}
      </>
    );
  };

  const errorCodeCell = (run: Run) => {
    const { errorCode } = run;
    if (!errorCode) return EMPTY_CELL;
    const explanation = translate(`salesforceIntegration.runs.errors.${errorCode}`, { _: "" });
    return (
      <>
        <Typography component="span" variant="body2" fontFamily="monospace">
          {errorCode}
        </Typography>
        {explanation !== "" && (
          <Typography variant="caption" display="block" color="text.secondary">
            {explanation}
          </Typography>
        )}
      </>
    );
  };

  let content;
  if (error) {
    content = <Alert severity="error">{translate("salesforceIntegration.runs.loadError")}</Alert>;
  } else if (loading && !runs) {
    content = (
      <Box sx={{ p: space.sm }}>
        <CircularProgress aria-label={translate("salesforceIntegration.runs.loading")} />
      </Box>
    );
  } else if (!runs || runs.length === 0) {
    content = (
      <Typography sx={{ p: 2 }} color="text.secondary">
        {translate("salesforceIntegration.runs.empty")}
      </Typography>
    );
  } else {
    content = (
      <TableContainer>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              {HEADER_KEYS.map(key => (
                <TableCell key={key}>{translate(`salesforceIntegration.runs.${key}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(run => (
              <TableRow key={run.id}>
                <TableCell>{formatCrmDateTime(run.startedAt)}</TableCell>
                <TableCell>{run.finishedAt ? formatCrmDateTime(run.finishedAt) : EMPTY_CELL}</TableCell>
                <TableCell>{translate(`salesforceIntegration.runs.triggers.${run.trigger}`)}</TableCell>
                <TableCell>
                  {run.mode ? translate(`salesforceIntegration.runs.modes.${run.mode}`) : EMPTY_CELL}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={translate(`salesforceIntegration.runs.statuses.${run.status}`)}
                    color={STATUS_COLOR[run.status]}
                  />
                  {run.phase &&
                    (run.status === SalesforceSyncStatus.Running || run.status === SalesforceSyncStatus.Failed) && (
                      <>
                        <Typography variant="caption" display="block">
                          {translate("salesforceIntegration.runs.progress", {
                            step: PHASES.indexOf(run.phase) + 1,
                            total: PHASES.length,
                            phase: translate(`salesforceIntegration.runs.phases.${run.phase}`),
                          })}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {translate("salesforceIntegration.runs.phaseRecords", {
                            count: formatCrmCount(run.phaseRecords),
                          })}
                        </Typography>
                        {run.progressAt && (
                          <Typography variant="caption" display="block">
                            {translate("salesforceIntegration.runs.progressAt", {
                              time: formatCrmDateTime(run.progressAt),
                            })}
                          </Typography>
                        )}
                      </>
                    )}
                </TableCell>
                <TableCell>
                  {translate(run.anonymized ? "salesforceIntegration.runs.yes" : "salesforceIntegration.runs.no")}
                </TableCell>
                <TableCell>
                  {recordsCell(run)}
                  {run.unavailable && run.unavailable.length > 0 && (
                    <Typography variant="caption" display="block" color="warning.main">
                      {translate("salesforceIntegration.runs.unavailable", { objects: run.unavailable.join(", ") })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{apiCallsCell(run)}</TableCell>
                <TableCell>{errorCodeCell(run)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={space.xs} mb={space.xs}>
        <Typography variant="h6">{title}</Typography>
        {canStart ? (
          startButtons
        ) : (
          <Tooltip title={translate("salesforceIntegration.runs.startUnavailable")}>
            <span>{startButtons}</span>
          </Tooltip>
        )}
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          borderColor: color.panelBorder,
          boxShadow: shadows.card,
          overflow: "hidden",
        }}
      >
        {content}
      </Paper>
      <CustomConfirm
        isOpen={confirmingFull}
        title="salesforceIntegration.runs.startFullTitle"
        content={translate("salesforceIntegration.runs.startFullConfirm")}
        onConfirm={handleStartFull}
        onClose={() => setConfirmingFull(false)}
      />
    </Box>
  );
};
